// Flow-line weights are recorded against SLOT INDEXES (a line from slot 2 to slot 5), with the two page names noted at the time. Slots are reassigned all the
// time - a drag-and-drop reorder, the top pages changing, a zoom that blanks the labels, the single-cluster view - and the old code answered every one of those
// by DELETING the weights whose page names no longer matched (or wiping everything on a reorder). Hours of counted traffic vanished with no way back.
//
// Now a path follows its PAGES: when the names at its slots change, its weight is moved to the slots those pages sit in now; if a page is not on the grid at the
// moment, the weight is PARKED under the page names and put back as soon as both pages are on the grid again. Nothing is deleted except by an explicit reset.
//
// Pure (the maps and the label lookup are passed in) so it can be tested without a browser.
export interface ParkedFlows {
  pairs: Map<string, number>;      // merged path, both directions: "<labelA>|<labelB>" with the two labels sorted
  directional: Map<string, number>; // one direction: "<from>|<to>"
  arrivals: Map<string, number>;   // a page's first landings: "<label>"
}

export const newParkedFlows = (): ParkedFlows => ({ pairs: new Map(), directional: new Map(), arrivals: new Map() });

export interface FlowStores {
  pathWeights: Map<string, number>;            // "lo-hi" | "arrival:i"
  directionalPathWeights: Map<string, number>; // "from->to"
  pathWeightLabels: Map<string, string>;       // key -> the labels it was recorded under ("A|B" or "A")
  parked: ParkedFlows;
}

const FALLBACK_LABEL = /^Page \d+$/; // getBoxLabel's name for a slot with no page: not a real page

export const currentFingerprint = (key: string, labelAt: (i: number) => string): string => {
  if (key.startsWith("arrival:")) {
    return labelAt(Number(key.slice("arrival:".length)));
  }
  const separator = key.includes("->") ? "->" : "-";
  const [a, b] = key.split(separator).map(Number);

  return `${labelAt(a)}|${labelAt(b)}`;
};

const add = (map: Map<string, number>, key: string, w: number) => map.set(key, (map.get(key) || 0) + w);

/**
 * Brings the stores in line with the slots as they are NOW. `slotCount` = how many slots can hold a page (indexes 0..slotCount-1).
 * Returns how many entries were moved or parked (for logging/tests).
 */
export const relocateFlows = (stores: FlowStores, labelAt: (i: number) => string, slotCount: number): number => {
  const indexOf = (label: string): number => {
    if (!label || FALLBACK_LABEL.test(label)) return -1;
    for (let i = 0; i < slotCount; i++) if (labelAt(i) === label) return i;

    return -1;
  };

  type Move = { kind: "path" | "dir" | "arrival"; key: string; label: string; w: number };
  const moves: Move[] = [];
  const take = (map: Map<string, number>, kind: Move["kind"]) => {
    for (const [key, w] of Array.from(map.entries())) {
      const recorded = stores.pathWeightLabels.get(key);
      if (recorded === undefined || recorded === currentFingerprint(key, labelAt)) continue;
      moves.push({ kind, key, label: recorded, w });
      map.delete(key);
    }
  };
  take(stores.pathWeights, "path");
  take(stores.directionalPathWeights, "dir");
  // arrivals live in pathWeights under "arrival:i"; take() above classed them as "path", so split them out by key
  for (const m of moves) if (m.key.startsWith("arrival:")) m.kind = "arrival";
  for (const m of moves) stores.pathWeightLabels.delete(m.key);

  const place = (kind: Move["kind"], label: string, w: number): boolean => {
    if (kind === "arrival") {
      const i = indexOf(label);
      if (i < 0) return false;
      const key = `arrival:${i}`;
      add(stores.pathWeights, key, w);
      stores.pathWeightLabels.set(key, labelAt(i));

      return true;
    }
    const [a, b] = label.split("|");
    const ia = indexOf(a), ib = indexOf(b);
    if (ia < 0 || ib < 0 || ia === ib) return false;
    if (kind === "dir") {
      const key = `${ia}->${ib}`;
      add(stores.directionalPathWeights, key, w);
      stores.pathWeightLabels.set(key, `${a}|${b}`);

      return true;
    }
    const [lo, hi] = ia < ib ? [ia, ib] : [ib, ia];
    const key = `${lo}-${hi}`;
    add(stores.pathWeights, key, w);
    stores.pathWeightLabels.set(key, `${labelAt(lo)}|${labelAt(hi)}`);

    return true;
  };

  const park = (kind: Move["kind"], label: string, w: number) => {
    if (kind === "arrival") add(stores.parked.arrivals, label, w);
    else if (kind === "dir") add(stores.parked.directional, label, w);
    else {
      const [a, b] = label.split("|");
      add(stores.parked.pairs, [a, b].sort().join("|"), w);
    }
  };

  for (const m of moves) if (!place(m.kind, m.label, m.w)) park(m.kind, m.label, m.w);

  // anything parked whose pages are all on the grid again comes back
  const restore = (map: Map<string, number>, kind: Move["kind"]) => {
    for (const [label, w] of Array.from(map.entries())) {
      if (place(kind, label, w)) {
        map.delete(label);
        moves.push({ kind, key: label, label, w });
      }
    }
  };
  restore(stores.parked.arrivals, "arrival");
  restore(stores.parked.directional, "dir");
  restore(stores.parked.pairs, "path");

  return moves.length;
};

export const clearParkedFlows = (p: ParkedFlows): void => {
  p.pairs.clear();
  p.directional.clear();
  p.arrivals.clear();
};
